import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Activity,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Download,
  FileStack,
  FileUp,
  Plus,
  Repeat2,
  Save,
  ShieldCheck,
  SlidersHorizontal,
  Users,
} from "lucide-react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { Counter } from "../components/atmos/Meters";
import {
  createAdminProfile,
  downloadAdminAttemptPdf,
  downloadAdminAttemptResponses,
  fetchCurrentProfile,
  formatTimestamp,
  importAdminProfileFromPdf,
  getAdminOverview,
  getAdminProfileEditor,
  getAdminProfiles,
  getAdminUserAttempts,
  getAdminUsers,
  getProfile,
  updateAdminProfile,
} from "../lib/profileEngine";

const EMPTY_OVERVIEW = {
  summary: {
    totalUsers: 0,
    newUsers7d: 0,
    totalAttempts: 0,
    completedProfiles: 0,
    repeatParticipants: 0,
    repeatAttempts: 0,
  },
  registrations: [],
  completions: [],
};

const DEFAULT_SCALE = [
  { value: 1, label: "Not at all" },
  { value: 2, label: "Slight degree" },
  { value: 3, label: "Minimal degree" },
  { value: 4, label: "Mild degree" },
  { value: 5, label: "Moderate degree" },
  { value: 6, label: "Average degree" },
  { value: 7, label: "Above average degree" },
  { value: 8, label: "Strong degree" },
  { value: 9, label: "Very strong degree" },
  { value: 10, label: "Highest degree" },
];

const inputClass =
  "w-full rounded-sm border border-line bg-black/10 px-3 py-2 text-base text-ink outline-none transition focus:border-gold/40";
const textAreaClass =
  "w-full rounded-sm border border-line bg-black/10 px-3 py-2 text-base text-ink outline-none transition focus:border-gold/40 min-h-[88px]";
const ghostButtonClass =
  "rounded-sm border border-line px-3 py-2 text-[16px] font-mono tracking-widest-2 text-ink-2 transition hover:border-gold/30 hover:text-gold";
const helperTextClass = "text-base text-ink";

const kpis = (summary) => [
  { label: "TOTAL USERS", value: summary.totalUsers, icon: Users },
  { label: "NEW USERS · 7D", value: summary.newUsers7d, icon: ShieldCheck },
  { label: "COMPLETED PROFILES", value: summary.completedProfiles, icon: BarChart3 },
  { label: "REPEAT ATTEMPTS", value: summary.repeatAttempts, icon: Repeat2 },
];

const createBlankProfile = () => ({
  info: {
    id: "",
    shortName: "",
    name: "",
    tagline: "",
    duration: "30-40 minutes",
    questionCount: 0,
    cooldownHours: 24,
    scoreModel: "dual",
    sortOrder: 100,
    sections: [
      {
        id: "section-a",
        label: "Section A",
        shortLabel: "A",
        range: [1, 1],
        prompt: "Describe the first section focus.",
      },
    ],
    instructions: ["Add the participant-facing introduction for this profile."],
    scale: DEFAULT_SCALE,
  },
  items: [
    {
      id: 1,
      text: "",
      sectionId: "section-a",
      sectionLabel: "Section A",
      scaleLabel: "1-10 scale",
    },
  ],
  factors: [],
  hasAttempts: false,
});

const parseNumberList = (value) =>
  value
    .split(",")
    .map((entry) => Number(entry.trim()))
    .filter((entry) => Number.isFinite(entry));

const toNumberListString = (values = []) => values.join(", ");

const replaceAt = (items, index, nextValue) => items.map((item, itemIndex) => (itemIndex === index ? nextValue : item));

const cleanProfilePayload = (draft) => {
  const sections = draft.info.sections
    .map((section) => ({
      ...section,
      id: section.id.trim(),
      label: section.label.trim(),
      shortLabel: section.shortLabel.trim(),
      prompt: section.prompt.trim(),
      range: [Number(section.range[0]), Number(section.range[1])],
    }))
    .filter((section) => section.id || section.label || section.prompt);

  const sectionLookup = new Map(sections.map((section) => [section.id, section.label]));

  const items = draft.items
    .map((item) => ({
      ...item,
      id: Number(item.id),
      text: item.text.trim(),
      sectionId: item.sectionId.trim(),
      sectionLabel: sectionLookup.get(item.sectionId.trim()) || item.sectionLabel || "",
      scaleLabel: item.scaleLabel.trim(),
    }))
    .filter((item) => item.text || item.sectionId)
    .sort((left, right) => left.id - right.id);

  const factors = draft.factors
    .map((factor) => ({
      ...factor,
      id: Number(factor.id),
      slug: factor.slug.trim(),
      name: factor.name.trim(),
      description: factor.description.trim(),
      primaryIds: [...factor.primaryIds].map(Number).filter(Number.isFinite),
      secondaryIds: [...factor.secondaryIds].map(Number).filter(Number.isFinite),
      primaryMid: factor.primaryMid.map(Number),
      secondaryMid: factor.secondaryMid.map(Number),
      varianceMid: factor.varianceMid.map(Number),
    }))
    .filter((factor) => factor.slug || factor.name)
    .sort((left, right) => left.id - right.id);

  return {
    info: {
      ...draft.info,
      id: draft.info.id.trim().toLowerCase().replace(/\s+/g, "-"),
      shortName: draft.info.shortName.trim().toUpperCase(),
      name: draft.info.name.trim(),
      tagline: draft.info.tagline.trim(),
      duration: draft.info.duration.trim(),
      questionCount: items.length,
      cooldownHours: Number(draft.info.cooldownHours),
      scoreModel: ["single", "dual"].includes(String(draft.info.scoreModel || "").toLowerCase())
        ? String(draft.info.scoreModel).toLowerCase()
        : "dual",
      sortOrder: Number(draft.info.sortOrder),
      sections,
      instructions: draft.info.instructions.map((entry) => entry.trim()).filter(Boolean),
      scale: draft.info.scale
        .map((option) => ({
          value: Number(option.value),
          label: option.label.trim(),
        }))
        .filter((option) => option.label)
        .sort((left, right) => left.value - right.value),
    },
    items,
    factors,
  };
};

const mergeImportedDraft = (current, imported) => ({
  info: {
    ...imported.info,
    id: current.info.id.trim() || imported.info.id,
    shortName: current.info.shortName.trim() || imported.info.shortName,
    name: imported.info.name || current.info.name,
    tagline: imported.info.tagline || current.info.tagline,
    duration: imported.info.duration || current.info.duration,
    cooldownHours: current.info.cooldownHours ?? imported.info.cooldownHours,
    scoreModel: imported.info.scoreModel || current.info.scoreModel || "dual",
    sortOrder: current.info.sortOrder ?? imported.info.sortOrder,
    sections: imported.info.sections?.length ? imported.info.sections : current.info.sections,
    instructions: imported.info.instructions?.length ? imported.info.instructions : current.info.instructions,
    scale: imported.info.scale?.length ? imported.info.scale : current.info.scale,
  },
  items: imported.items?.length ? imported.items : current.items,
  factors: imported.factors?.length ? imported.factors : current.factors,
  hasAttempts: current.hasAttempts,
});

const AdminDashboard = () => {
  const profile = getProfile();
  const isSuperAdmin = Boolean(profile.isSuperAdmin);
  const [activeTab, setActiveTab] = useState("overview");
  const [overview, setOverview] = useState(EMPTY_OVERVIEW);
  const [users, setUsers] = useState([]);
  const [profileCatalog, setProfileCatalog] = useState([]);
  const [editor, setEditor] = useState(createBlankProfile());
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [studioLoading, setStudioLoading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [importingPdf, setImportingPdf] = useState(false);
  const [profilePdfFile, setProfilePdfFile] = useState(null);
  const [scoringPdfFile, setScoringPdfFile] = useState(null);
  const [importMeta, setImportMeta] = useState(null);
  const [error, setError] = useState("");
  const [studioError, setStudioError] = useState("");
  const [studioMessage, setStudioMessage] = useState("");
  const [accessDenied, setAccessDenied] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [attemptsByUser, setAttemptsByUser] = useState({});
  const [attemptsLoadingUserId, setAttemptsLoadingUserId] = useState(null);
  const [attemptsError, setAttemptsError] = useState("");
  const [downloadingAttemptId, setDownloadingAttemptId] = useState(null);

  const toggleUserAttempts = async (userId) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      return;
    }

    setExpandedUserId(userId);
    setAttemptsError("");

    if (attemptsByUser[userId]) {
      return;
    }

    setAttemptsLoadingUserId(userId);
    try {
      const result = await getAdminUserAttempts(userId);
      setAttemptsByUser((previous) => ({ ...previous, [userId]: result.attempts || [] }));
    } catch (err) {
      setAttemptsError(err.message || "Unable to load reports for this participant.");
    } finally {
      setAttemptsLoadingUserId(null);
    }
  };

  const handleDownloadAdminPdf = async (userId, attemptId) => {
    setDownloadingAttemptId(`${attemptId}-pdf`);
    try {
      await downloadAdminAttemptPdf(userId, attemptId);
    } catch (err) {
      setAttemptsError(err.message || "Unable to download report PDF.");
    } finally {
      setDownloadingAttemptId(null);
    }
  };

  const handleDownloadAdminCsv = async (userId, attemptId) => {
    setDownloadingAttemptId(`${attemptId}-csv`);
    try {
      await downloadAdminAttemptResponses(userId, attemptId);
    } catch (err) {
      setAttemptsError(err.message || "Unable to download the question-answer sheet.");
    } finally {
      setDownloadingAttemptId(null);
    }
  };

  const repeatUsers = useMemo(
    () => users.filter((user) => (user.sameProfileAttempts || 0) > 1).length,
    [users],
  );

  const loadProfileCatalog = async (profileIdToOpen = null) => {
    const profiles = await getAdminProfiles();
    setProfileCatalog(profiles);

    if (profileIdToOpen) {
      const loaded = await getAdminProfileEditor(profileIdToOpen);
      setSelectedProfileId(profileIdToOpen);
      setEditor(loaded);
      return;
    }

    if (!selectedProfileId && profiles.length > 0) {
      const firstProfileId = profiles[0].id;
      const loaded = await getAdminProfileEditor(firstProfileId);
      setSelectedProfileId(firstProfileId);
      setEditor(loaded);
    }
  };

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      setLoading(true);
      setError("");
      setStudioError("");
      setAccessDenied(false);
      try {
        const currentUser = await fetchCurrentProfile();
        const [overviewResponse, usersResponse] = await Promise.all([
          getAdminOverview(),
          getAdminUsers(),
        ]);
        if (ignore) return;

        setOverview({ ...EMPTY_OVERVIEW, ...overviewResponse });
        setUsers(usersResponse.users || []);

        if (currentUser?.isSuperAdmin) {
          const profilesResponse = await getAdminProfiles();
          if (ignore) return;
          setProfileCatalog(profilesResponse || []);

          if (profilesResponse?.length) {
            const loaded = await getAdminProfileEditor(profilesResponse[0].id);
            if (!ignore) {
              setSelectedProfileId(profilesResponse[0].id);
              setEditor(loaded);
            }
          } else if (!ignore) {
            setEditor(createBlankProfile());
          }
        }
      } catch (err) {
        if (!ignore) {
          if (err.status === 403) {
            setAccessDenied(true);
          } else {
            setError(err.message || "Unable to load the admin dashboard.");
          }
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      ignore = true;
    };
  }, []);

  const openProfile = async (profileId) => {
    setStudioLoading(true);
    setStudioError("");
    setStudioMessage("");
    try {
      const loaded = await getAdminProfileEditor(profileId);
      setSelectedProfileId(profileId);
      setEditor(loaded);
    } catch (err) {
      setStudioError(err.message || "Unable to load this profile definition.");
    } finally {
      setStudioLoading(false);
    }
  };

  const startNewProfile = () => {
    setSelectedProfileId(null);
    setStudioError("");
    setStudioMessage("");
    setImportMeta(null);
    setProfilePdfFile(null);
    setScoringPdfFile(null);
    setEditor(createBlankProfile());
  };

  const importFromPdf = async () => {
    setImportingPdf(true);
    setStudioError("");
    setStudioMessage("");
    try {
      const result = await importAdminProfileFromPdf({
        profilePdf: profilePdfFile,
        scoringPdf: scoringPdfFile,
      });
      setEditor((current) => mergeImportedDraft(current, result.profile));
      setImportMeta(result.meta || null);
      setStudioMessage("PDF import completed. Review the extracted draft and save when ready.");
    } catch (err) {
      setStudioError(err.message || "Unable to import the uploaded PDFs.");
    } finally {
      setImportingPdf(false);
    }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    setStudioError("");
    setStudioMessage("");
    try {
      const payload = cleanProfilePayload(editor);
      const savedProfile = selectedProfileId
        ? await updateAdminProfile(selectedProfileId, payload)
        : await createAdminProfile(payload);

      await loadProfileCatalog(savedProfile.info.id);
      setSelectedProfileId(savedProfile.info.id);
      setEditor(savedProfile);
      setStudioMessage(
        selectedProfileId
          ? "Profile details saved successfully."
          : "New profile created successfully.",
      );
    } catch (err) {
      setStudioError(err.message || "Unable to save this profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  const updateInfo = (field, value) => {
    setEditor((current) => ({
      ...current,
      info: {
        ...current.info,
        [field]: value,
      },
    }));
  };

  const updateInstruction = (index, value) => {
    setEditor((current) => ({
      ...current,
      info: {
        ...current.info,
        instructions: replaceAt(current.info.instructions, index, value),
      },
    }));
  };

  const addInstruction = () => {
    setEditor((current) => ({
      ...current,
      info: {
        ...current.info,
        instructions: [...current.info.instructions, ""],
      },
    }));
  };

  const removeInstruction = (index) => {
    setEditor((current) => ({
      ...current,
      info: {
        ...current.info,
        instructions: current.info.instructions.filter((_, currentIndex) => currentIndex !== index),
      },
    }));
  };

  const updateSection = (index, field, value) => {
    setEditor((current) => ({
      ...current,
      info: {
        ...current.info,
        sections: replaceAt(current.info.sections, index, {
          ...current.info.sections[index],
          [field]: value,
        }),
      },
    }));
  };

  const updateSectionRange = (index, rangeIndex, value) => {
    setEditor((current) => {
      const section = current.info.sections[index];
      const nextRange = [...section.range];
      nextRange[rangeIndex] = Number(value);
      return {
        ...current,
        info: {
          ...current.info,
          sections: replaceAt(current.info.sections, index, {
            ...section,
            range: nextRange,
          }),
        },
      };
    });
  };

  const addSection = () => {
    setEditor((current) => {
      const nextIndex = current.info.sections.length + 1;
      return {
        ...current,
        info: {
          ...current.info,
          sections: [
            ...current.info.sections,
            {
              id: `section-${String.fromCharCode(96 + Math.min(nextIndex, 26))}`,
              label: `Section ${nextIndex}`,
              shortLabel: String.fromCharCode(64 + Math.min(nextIndex, 26)),
              range: [current.items.length + 1, current.items.length + 1],
              prompt: "Describe this section focus.",
            },
          ],
        },
      };
    });
  };

  const removeSection = (index) => {
    setEditor((current) => ({
      ...current,
      info: {
        ...current.info,
        sections: current.info.sections.filter((_, currentIndex) => currentIndex !== index),
      },
    }));
  };

  const updateScale = (index, field, value) => {
    setEditor((current) => ({
      ...current,
      info: {
        ...current.info,
        scale: replaceAt(current.info.scale, index, {
          ...current.info.scale[index],
          [field]: field === "value" ? Number(value) : value,
        }),
      },
    }));
  };

  const addScaleOption = () => {
    setEditor((current) => ({
      ...current,
      info: {
        ...current.info,
        scale: [
          ...current.info.scale,
          {
            value: current.info.scale.length ? Math.max(...current.info.scale.map((entry) => entry.value)) + 1 : 1,
            label: "",
          },
        ],
      },
    }));
  };

  const removeScaleOption = (index) => {
    setEditor((current) => ({
      ...current,
      info: {
        ...current.info,
        scale: current.info.scale.filter((_, currentIndex) => currentIndex !== index),
      },
    }));
  };

  const updateItem = (index, field, value) => {
    setEditor((current) => ({
      ...current,
      items: replaceAt(current.items, index, {
        ...current.items[index],
        [field]: field === "id" ? Number(value) : value,
      }),
    }));
  };

  const addItem = () => {
    setEditor((current) => {
      const nextId = current.items.length ? Math.max(...current.items.map((item) => item.id)) + 1 : 1;
      const firstSection = current.info.sections[0];
      return {
        ...current,
        items: [
          ...current.items,
          {
            id: nextId,
            text: "",
            sectionId: firstSection?.id || "",
            sectionLabel: firstSection?.label || "",
            scaleLabel: "1-10 scale",
          },
        ],
      };
    });
  };

  const removeItem = (index) => {
    setEditor((current) => ({
      ...current,
      items: current.items.filter((_, currentIndex) => currentIndex !== index),
    }));
  };

  const updateFactor = (index, field, value) => {
    setEditor((current) => ({
      ...current,
      factors: replaceAt(current.factors, index, {
        ...current.factors[index],
        [field]: field === "id" ? Number(value) : value,
      }),
    }));
  };

  const updateFactorRange = (index, field, rangeIndex, value) => {
    setEditor((current) => {
      const factor = current.factors[index];
      const nextRange = [...factor[field]];
      nextRange[rangeIndex] = Number(value);
      return {
        ...current,
        factors: replaceAt(current.factors, index, {
          ...factor,
          [field]: nextRange,
        }),
      };
    });
  };

  const updateFactorIds = (index, field, value) => {
    setEditor((current) => ({
      ...current,
      factors: replaceAt(current.factors, index, {
        ...current.factors[index],
        [field]: parseNumberList(value),
      }),
    }));
  };

  const addFactor = () => {
    setEditor((current) => {
      const nextId = current.factors.length ? Math.max(...current.factors.map((factor) => factor.id)) + 1 : 1;
      return {
        ...current,
        factors: [
          ...current.factors,
          {
            id: nextId,
            slug: `factor-${nextId}`,
            name: "",
            description: "",
            primaryIds: [],
            secondaryIds: [],
            primaryMid: [40, 69],
            secondaryMid: [40, 69],
            varianceMid: [-10, 10],
          },
        ],
      };
    });
  };

  const removeFactor = (index) => {
    setEditor((current) => ({
      ...current,
      factors: current.factors.filter((_, currentIndex) => currentIndex !== index),
    }));
  };

  if (accessDenied) {
    return (
      <main className="relative pt-8 md:pt-10 lg:pt-24 pb-12 md:pb-14 px-5 md:px-10 lg:px-16 lg:pl-32 xl:pl-36">
        <div className="glass rounded-sm p-6 md:p-8 max-w-3xl">
          <div className="font-mono text-[16px] tracking-widest-2 text-ink mb-3">◇ ADMIN ACCESS</div>
          <h1 className="font-serif text-4xl md:text-5xl text-ink">Admin access is not enabled for this account.</h1>
          <p className="text-ink-2 mt-4">
            Sign in with the admin account to view registrations, manage profiles, and edit the profile library.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="relative pt-8 md:pt-10 lg:pt-24 pb-12 md:pb-14 px-5 md:px-10 lg:px-16 lg:pl-32 xl:pl-36" data-testid="admin-dashboard">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="mb-8 md:mb-10"
      >
        <div className="font-mono text-[16px] tracking-widest-2 text-ink mb-3">◇ ADMIN CONTROL ROOM · {profile.fullName || "ADMIN"}</div>
        <h1 className="font-serif text-4xl md:text-5xl xl:text-6xl tracking-tighter leading-[1.02] text-ink">
          Analytics, registrations,
          <br />
          <span className="italic text-gold gold-glow">and profile authoring.</span>
        </h1>
        <p className="text-ink-2 mt-5 max-w-3xl">
          This admin console shows live platform metrics and includes a profile studio where new profiles,
          item statements, scoring factors, and participant-facing guidance can be created and managed in one place.
        </p>
      </motion.div>

      {isSuperAdmin ? (
        <div className="flex flex-wrap gap-3 mb-8">
          {[
            { id: "overview", label: "Overview" },
            { id: "studio", label: "Profile Studio" },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`rounded-full border px-4 py-2 font-mono text-[16px] tracking-widest-2 transition-colors ${
                activeTab === tab.id
                  ? "text-gold border-[color:var(--chip-active-border)] bg-[color:var(--chip-active-bg)]"
                  : "border-line text-ink-2 hover:border-line-2 hover:text-ink"
              }`}
            >
              {tab.label.toUpperCase()}
            </button>
          ))}
        </div>
      ) : null}

      {activeTab !== "studio" ? (
        <>
      {error ? <div className="glass rounded-sm p-5 mb-8 text-[color:var(--warn-text)]">{error}</div> : null}

      <section className="grid grid-cols-12 gap-4 mb-8">
        {kpis(overview.summary).map((item) => (
          <div key={item.label} className="col-span-12 sm:col-span-6 xl:col-span-3 glass rounded-sm p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-mono text-[16px] tracking-widest-2 text-ink">{item.label}</div>
                <div className="font-mono text-4xl text-gold gold-glow tabular mt-3">
                  <Counter value={item.value} />
                </div>
              </div>
              <item.icon className="w-5 h-5 text-gold mt-1" />
            </div>
          </div>
        ))}
      </section>

      <section className="grid grid-cols-12 gap-6 mb-8">
        <div className="col-span-12 xl:col-span-7 glass rounded-sm p-6">
          <div className="font-mono text-[16px] tracking-widest-2 text-ink mb-2">◇ NEW USER REGISTRATIONS · 14 DAYS</div>
          <h2 className="font-serif text-3xl text-ink mb-5">Registration trend</h2>
          <div className="h-[280px]">
            <ResponsiveContainer>
              <AreaChart data={overview.registrations}>
                <defs>
                  <linearGradient id="adminRegistrations" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#a0b18a" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#a0b18a" stopOpacity={0.04} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="day" stroke="rgba(255,255,255,0.35)" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} stroke="rgba(255,255,255,0.35)" tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    background: "rgba(18,18,18,0.96)",
                    border: "1px solid rgba(160,177,138,0.32)",
                    fontSize: 12,
                  }}
                />
                <Area type="monotone" dataKey="registrations" stroke="#a0b18a" strokeWidth={2} fill="url(#adminRegistrations)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-span-12 xl:col-span-5 glass rounded-sm p-6">
          <div className="font-mono text-[16px] tracking-widest-2 text-ink mb-2">◇ PROFILE COMPLETIONS · 14 DAYS</div>
          <h2 className="font-serif text-3xl text-ink mb-5">Completion trend</h2>
          <div className="h-[280px]">
            <ResponsiveContainer>
              <AreaChart data={overview.completions}>
                <defs>
                  <linearGradient id="adminCompletions" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#7a8fb8" stopOpacity={0.48} />
                    <stop offset="100%" stopColor="#7a8fb8" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="day" stroke="rgba(255,255,255,0.35)" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} stroke="rgba(255,255,255,0.35)" tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    background: "rgba(18,18,18,0.96)",
                    border: "1px solid rgba(122,143,184,0.32)",
                    fontSize: 12,
                  }}
                />
                <Area type="monotone" dataKey="completions" stroke="#7a8fb8" strokeWidth={2} fill="url(#adminCompletions)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      <section className="mb-8">
        <div className="glass rounded-sm p-6">
          <div className="font-mono text-[16px] tracking-widest-2 text-ink mb-2">◇ ATTEMPT SUMMARY</div>
          <h2 className="font-serif text-3xl text-ink mb-5">Profile activity</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Total profile attempts", value: overview.summary.totalAttempts },
              { label: "Completed profiles", value: overview.summary.completedProfiles },
              { label: "Participants with repeats", value: overview.summary.repeatParticipants || repeatUsers },
              { label: "Same-profile attempts", value: overview.summary.repeatAttempts },
            ].map((item) => (
              <div key={item.label} className="border border-line rounded-sm p-4">
                <div className="text-ink-2 text-base">{item.label}</div>
                <div className="font-mono text-3xl text-gold gold-glow mt-2 tabular">
                  <Counter value={item.value} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mb-8">
        <div className="glass rounded-sm p-6">
          <div className="font-mono text-[16px] tracking-widest-2 text-ink mb-2">◇ PARTICIPANT DETAIL</div>
          <div className="flex items-center justify-between gap-4 mb-5">
            <h2 className="font-serif text-3xl text-ink">User details</h2>
            <div className="text-ink-2 text-base flex items-center gap-2">
              <Activity className="w-4 h-4 text-gold" />
              {loading ? "Loading users..." : `${users.length} registered users`}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[920px] text-base">
              <thead className="text-[16px] font-mono tracking-widest-2 text-ink">
                <tr className="text-left border-b border-line">
                  <th className="py-3 pr-4 font-normal">NAME</th>
                  <th className="py-3 pr-4 font-normal">EMAIL</th>
                  <th className="py-3 pr-4 font-normal">ROLE</th>
                  <th className="py-3 pr-4 font-normal">REGISTERED</th>
                  <th className="py-3 pr-4 font-normal">ATTEMPTS</th>
                  <th className="py-3 pr-4 font-normal">COMPLETED</th>
                  <th className="py-3 pr-4 font-normal">SAME PROFILE</th>
                  <th className="py-3 pr-4 font-normal">LAST ACTIVITY</th>
                  <th className="py-3 pr-4 font-normal">REPORTS</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const isExpanded = expandedUserId === user.id;
                  const attempts = attemptsByUser[user.id] || [];
                  return (
                    <React.Fragment key={user.id}>
                      <tr className="border-b border-line/60 hover:bg-white/[0.02]">
                        <td className="py-3 pr-4 text-ink">{user.fullName}</td>
                        <td className="py-3 pr-4 text-ink-2 font-mono text-base">{user.email}</td>
                        <td className="py-3 pr-4">
                          <span className="text-[16px] font-mono tracking-widest-2 px-2 py-1 rounded-sm border border-line text-ink-2">
                            {(user.role || "participant").toUpperCase()}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-ink-2">{formatTimestamp(user.createdAt)}</td>
                        <td className="py-3 pr-4 font-mono text-gold tabular">{user.totalAttempts || 0}</td>
                        <td className="py-3 pr-4 font-mono text-gold tabular">{user.completedProfiles || 0}</td>
                        <td className="py-3 pr-4 font-mono text-gold tabular">{user.sameProfileAttempts || 0}</td>
                        <td className="py-3 pr-4 text-ink-2">{user.lastActivityAt ? formatTimestamp(user.lastActivityAt) : "No activity"}</td>
                        <td className="py-3 pr-4">
                          <button
                            type="button"
                            onClick={() => toggleUserAttempts(user.id)}
                            className="inline-flex items-center gap-1.5 rounded-sm border border-line px-2.5 py-1.5 text-[16px] font-mono tracking-widest-2 text-ink-2 transition hover:border-gold/30 hover:text-gold"
                          >
                            {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                            VIEW
                          </button>
                        </td>
                      </tr>
                      {isExpanded ? (
                        <tr className="border-b border-line/60 bg-black/10">
                          <td colSpan={9} className="py-4 px-4">
                            {attemptsLoadingUserId === user.id ? (
                              <div className="text-ink-2 text-base">Loading reports...</div>
                            ) : attemptsError ? (
                              <div className="text-[color:var(--warn-text)] text-base">{attemptsError}</div>
                            ) : attempts.length === 0 ? (
                              <div className="text-ink-2 text-base">No profile attempts recorded for this participant.</div>
                            ) : (
                              <div className="space-y-2">
                                {attempts.map((attempt) => (
                                  <div
                                    key={attempt.id}
                                    className="flex flex-wrap items-center justify-between gap-3 rounded-sm border border-line px-3 py-2.5"
                                  >
                                    <div className="min-w-0">
                                      <div className="text-ink text-base">{attempt.profileName}</div>
                                      <div className="font-mono text-[16px] tracking-widest-2 text-ink-2 mt-1">
                                        {attempt.status.toUpperCase()}
                                        {attempt.completedAt ? ` · ${formatTimestamp(attempt.completedAt)}` : ""}
                                      </div>
                                    </div>
                                    {attempt.status === "completed" ? (
                                      <div className="flex items-center gap-2 shrink-0">
                                        <button
                                          type="button"
                                          onClick={() => handleDownloadAdminPdf(user.id, attempt.id)}
                                          disabled={downloadingAttemptId === `${attempt.id}-pdf`}
                                          className="inline-flex items-center gap-1.5 rounded-sm border border-line px-2.5 py-1.5 text-[16px] font-mono tracking-widest-2 text-ink-2 transition hover:border-gold/30 hover:text-gold disabled:opacity-50"
                                        >
                                          <Download className="w-3.5 h-3.5" />
                                          PDF
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleDownloadAdminCsv(user.id, attempt.id)}
                                          disabled={downloadingAttemptId === `${attempt.id}-csv`}
                                          className="inline-flex items-center gap-1.5 rounded-sm border border-line px-2.5 py-1.5 text-[16px] font-mono tracking-widest-2 text-ink-2 transition hover:border-gold/30 hover:text-gold disabled:opacity-50"
                                        >
                                          <Download className="w-3.5 h-3.5" />
                                          Q&amp;A CSV
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="text-ink-2 text-base shrink-0">Report available after completion</div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>
        </>
      ) : null}

      {activeTab === "studio" && isSuperAdmin ? (
        <>
      <section className="grid grid-cols-12 gap-6">
        <aside className="col-span-12 xl:col-span-3 glass rounded-sm p-5 md:p-6 h-fit">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <div className="font-mono text-[16px] tracking-widest-2 text-ink mb-2">◇ PROFILE STUDIO</div>
              <h2 className="font-serif text-3xl text-ink">Profile library</h2>
            </div>
            <FileStack className="w-5 h-5 text-gold mt-1" />
          </div>

          <button type="button" onClick={startNewProfile} className="btn-gold w-full justify-center mb-5">
            <Plus className="w-4 h-4" />
            New Profile
          </button>

          <div className="space-y-3">
            {profileCatalog.map((catalogProfile) => (
              <button
                key={catalogProfile.id}
                type="button"
                onClick={() => openProfile(catalogProfile.id)}
                className={`w-full text-left rounded-sm border px-4 py-4 transition ${
                  selectedProfileId === catalogProfile.id
                    ? "border-[color:var(--chip-active-border)] bg-[color:var(--chip-active-bg)]"
                    : "border-line hover:border-line-2 hover:bg-white/[0.02]"
                }`}
              >
                <div className="font-mono text-[16px] tracking-widest-2 text-gold mb-2">{catalogProfile.shortName}</div>
                <div className="font-serif text-2xl text-ink leading-tight">{catalogProfile.name}</div>
                <div className="text-base text-ink-2 mt-3">
                  {catalogProfile.questionCount} items · {catalogProfile.factorCount} factors
                </div>
              </button>
            ))}
          </div>
        </aside>

        <div className="col-span-12 xl:col-span-9 glass rounded-sm p-5 md:p-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5 mb-6">
            <div>
              <div className="font-mono text-[16px] tracking-widest-2 text-ink mb-2">◇ PROFILE EDITOR</div>
              <h2 className="font-serif text-3xl md:text-4xl text-ink">
                {selectedProfileId ? `Editing ${editor.info.name || editor.info.id}` : "Create a new profile"}
              </h2>
              <p className="text-ink-2 mt-3 max-w-3xl">
                Manage profile information, item statements, sections, scale options, and factor mappings from this screen.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <div className="glass rounded-sm px-4 py-3 text-base text-ink-2 flex items-center gap-3">
                <SlidersHorizontal className="w-4 h-4 text-gold" />
                {editor.items.length} items · {editor.factors.length} factors
              </div>
              <button
                type="button"
                onClick={saveProfile}
                disabled={savingProfile || studioLoading}
                className="btn-gold justify-center disabled:opacity-60"
              >
                <Save className="w-4 h-4" />
                {savingProfile ? "Saving..." : "Save profile"}
              </button>
            </div>
          </div>

          {studioError ? <div className="glass rounded-sm p-4 mb-5 text-[color:var(--warn-text)]">{studioError}</div> : null}
          {studioMessage ? <div className="glass rounded-sm p-4 mb-5 text-[color:var(--success-text)]">{studioMessage}</div> : null}
          {editor.hasAttempts ? (
            <div className="glass rounded-sm p-4 mb-5 text-amber-100">
              This profile already has participant attempts. Existing sections, items, and factors cannot be removed,
              but you can still update labels, prompts, scale wording, and related descriptive content.
            </div>
          ) : null}
          {studioLoading ? <div className="glass rounded-sm p-4 mb-5 text-ink-2">Loading profile editor...</div> : null}

          <div className="grid grid-cols-12 gap-5">
            <section className="col-span-12 glass rounded-sm p-5">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5 mb-4">
                <div>
                  <div className="font-mono text-[16px] tracking-widest-2 text-ink mb-2">◇ PDF ASSISTED IMPORT</div>
                  <h3 className="font-serif text-3xl text-ink">Upload source PDFs into the draft editor</h3>
                  <p className="text-ink-2 mt-3 max-w-3xl">
                    Upload the profile PDF to extract numbered statements, and optionally upload the scoring algorithm PDF
                    to suggest factor names and item mappings. You can still continue adding or editing everything manually below.
                  </p>
                </div>
                <div className="glass rounded-sm px-4 py-3 text-base text-ink-2 flex items-center gap-3 h-fit">
                  <FileUp className="w-4 h-4 text-gold" />
                  Import first, then review, then save to the profile library
                </div>
              </div>

              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-6">
                  <label className={helperTextClass}>Profile PDF</label>
                  <input
                    className={inputClass}
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={(event) => setProfilePdfFile(event.target.files?.[0] || null)}
                  />
                  <div className="mt-2 text-base text-ink">
                    {profilePdfFile ? `Selected: ${profilePdfFile.name}` : "Upload the profile booklet or statement PDF."}
                  </div>
                </div>
                <div className="col-span-12 md:col-span-6">
                  <label className={helperTextClass}>Scoring algorithm PDF</label>
                  <input
                    className={inputClass}
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={(event) => setScoringPdfFile(event.target.files?.[0] || null)}
                  />
                  <div className="mt-2 text-base text-ink">
                    {scoringPdfFile ? `Selected: ${scoringPdfFile.name}` : "Optional. Upload the scoring logic PDF to suggest factor mappings."}
                  </div>
                </div>
                <div className="col-span-12 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={importFromPdf}
                    disabled={importingPdf || (!profilePdfFile && !scoringPdfFile)}
                    className="btn-gold justify-center disabled:opacity-60"
                  >
                    <FileUp className="w-4 h-4" />
                    {importingPdf ? "Importing..." : "Import PDFs into Draft"}
                  </button>
                  <div className="text-base text-ink-2 flex items-center">
                    Manual editing remains available even if you do not upload any PDFs.
                  </div>
                </div>
              </div>

              {importMeta ? (
                <div className="mt-5 border border-line rounded-sm p-4">
                  <div className="font-mono text-[16px] tracking-widest-2 text-ink mb-3">◇ IMPORT SUMMARY</div>
                  <div className="grid grid-cols-12 gap-4 mb-4">
                    <div className="col-span-12 md:col-span-3 glass rounded-sm p-4">
                      <div className="text-base text-ink">Statements extracted</div>
                      <div className="font-mono text-3xl text-gold tabular mt-2">{importMeta.itemCount || 0}</div>
                    </div>
                    <div className="col-span-12 md:col-span-3 glass rounded-sm p-4">
                      <div className="text-base text-ink">Factors suggested</div>
                      <div className="font-mono text-3xl text-gold tabular mt-2">{importMeta.factorCount || 0}</div>
                    </div>
                    <div className="col-span-12 md:col-span-6 glass rounded-sm p-4">
                      <div className="text-base text-ink">Warnings</div>
                      <div className="text-base text-ink mt-2">
                        {importMeta.warnings?.length ? importMeta.warnings.length : "No warnings"}
                      </div>
                    </div>
                  </div>
                  {importMeta.warnings?.length ? (
                    <div className="space-y-2">
                      {importMeta.warnings.map((warning, index) => (
                        <div key={`warning-${index}`} className="text-base text-amber-100">
                          {warning}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </section>

            <section className="col-span-12 glass rounded-sm p-5">
              <div className="font-mono text-[16px] tracking-widest-2 text-ink mb-4">◇ PROFILE INFORMATION</div>
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-3">
                  <label className={helperTextClass}>Profile code</label>
                  <input
                    className={inputClass}
                    value={editor.info.id}
                    onChange={(event) => updateInfo("id", event.target.value)}
                    disabled={Boolean(selectedProfileId)}
                  />
                </div>
                <div className="col-span-12 md:col-span-2">
                  <label className={helperTextClass}>Short name</label>
                  <input className={inputClass} value={editor.info.shortName} onChange={(event) => updateInfo("shortName", event.target.value)} />
                </div>
                <div className="col-span-12 md:col-span-7">
                  <label className={helperTextClass}>Display name</label>
                  <input className={inputClass} value={editor.info.name} onChange={(event) => updateInfo("name", event.target.value)} />
                </div>
                <div className="col-span-12">
                  <label className={helperTextClass}>Tagline</label>
                  <textarea className={textAreaClass} value={editor.info.tagline} onChange={(event) => updateInfo("tagline", event.target.value)} />
                </div>
                <div className="col-span-12 md:col-span-4">
                  <label className={helperTextClass}>Duration label</label>
                  <input className={inputClass} value={editor.info.duration} onChange={(event) => updateInfo("duration", event.target.value)} />
                </div>
                <div className="col-span-12 md:col-span-2">
                  <label className={helperTextClass}>Cooldown hours</label>
                  <input
                    className={inputClass}
                    type="number"
                    value={editor.info.cooldownHours}
                    onChange={(event) => updateInfo("cooldownHours", Number(event.target.value))}
                  />
                </div>
                <div className="col-span-12 md:col-span-2">
                  <label className={helperTextClass}>Scoring model</label>
                  <select
                    className={inputClass}
                    value={editor.info.scoreModel || "dual"}
                    onChange={(event) => updateInfo("scoreModel", event.target.value)}
                  >
                    <option value="dual">Dual score</option>
                    <option value="single">Single score</option>
                  </select>
                </div>
                <div className="col-span-12 md:col-span-2">
                  <label className={helperTextClass}>Sort order</label>
                  <input
                    className={inputClass}
                    type="number"
                    value={editor.info.sortOrder}
                    onChange={(event) => updateInfo("sortOrder", Number(event.target.value))}
                  />
                </div>
                <div className="col-span-12 md:col-span-2">
                  <label className={helperTextClass}>Item count</label>
                  <div className={`${inputClass} flex items-center`}>{editor.items.length}</div>
                </div>
              </div>
            </section>

            <section className="col-span-12 glass rounded-sm p-5">
              <div className="flex items-center justify-between gap-4 mb-4">
                <div className="font-mono text-[16px] tracking-widest-2 text-ink">◇ PARTICIPANT INSTRUCTIONS</div>
                <button type="button" onClick={addInstruction} className={ghostButtonClass}>
                  Add instruction
                </button>
              </div>
              <div className="space-y-4">
                {editor.info.instructions.map((instruction, index) => (
                  <div key={`instruction-${index}`} className="grid grid-cols-[1fr_auto] gap-3 items-start">
                    <textarea
                      className={textAreaClass}
                      value={instruction}
                      onChange={(event) => updateInstruction(index, event.target.value)}
                    />
                    <button type="button" onClick={() => removeInstruction(index)} className={ghostButtonClass}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </section>

            <section className="col-span-12 glass rounded-sm p-5">
              <div className="flex items-center justify-between gap-4 mb-4">
                <div className="font-mono text-[16px] tracking-widest-2 text-ink">◇ PROFILE SECTIONS</div>
                <button type="button" onClick={addSection} className={ghostButtonClass}>
                  Add section
                </button>
              </div>
              <div className="space-y-5">
                {editor.info.sections.map((section, index) => (
                  <div key={`section-${index}`} className="border border-line rounded-sm p-4">
                    <div className="grid grid-cols-12 gap-4">
                      <div className="col-span-12 md:col-span-2">
                        <label className={helperTextClass}>Section id</label>
                        <input className={inputClass} value={section.id} onChange={(event) => updateSection(index, "id", event.target.value)} />
                      </div>
                      <div className="col-span-12 md:col-span-4">
                        <label className={helperTextClass}>Label</label>
                        <input className={inputClass} value={section.label} onChange={(event) => updateSection(index, "label", event.target.value)} />
                      </div>
                      <div className="col-span-12 md:col-span-2">
                        <label className={helperTextClass}>Short label</label>
                        <input className={inputClass} value={section.shortLabel} onChange={(event) => updateSection(index, "shortLabel", event.target.value)} />
                      </div>
                      <div className="col-span-6 md:col-span-2">
                        <label className={helperTextClass}>Start item</label>
                        <input
                          className={inputClass}
                          type="number"
                          value={section.range[0]}
                          onChange={(event) => updateSectionRange(index, 0, event.target.value)}
                        />
                      </div>
                      <div className="col-span-6 md:col-span-2">
                        <label className={helperTextClass}>End item</label>
                        <input
                          className={inputClass}
                          type="number"
                          value={section.range[1]}
                          onChange={(event) => updateSectionRange(index, 1, event.target.value)}
                        />
                      </div>
                      <div className="col-span-12">
                        <label className={helperTextClass}>Prompt</label>
                        <textarea className={textAreaClass} value={section.prompt} onChange={(event) => updateSection(index, "prompt", event.target.value)} />
                      </div>
                    </div>
                    <div className="mt-3 flex justify-end">
                      <button type="button" onClick={() => removeSection(index)} className={ghostButtonClass}>
                        Remove section
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="col-span-12 glass rounded-sm p-5">
              <div className="flex items-center justify-between gap-4 mb-4">
                <div className="font-mono text-[16px] tracking-widest-2 text-ink">◇ SCALE OPTIONS</div>
                <button type="button" onClick={addScaleOption} className={ghostButtonClass}>
                  Add scale option
                </button>
              </div>
              <div className="space-y-3">
                {editor.info.scale.map((option, index) => (
                  <div key={`scale-${index}`} className="grid grid-cols-[120px_1fr_auto] gap-3 items-center">
                    <input
                      className={inputClass}
                      type="number"
                      value={option.value}
                      onChange={(event) => updateScale(index, "value", event.target.value)}
                    />
                    <input className={inputClass} value={option.label} onChange={(event) => updateScale(index, "label", event.target.value)} />
                    <button type="button" onClick={() => removeScaleOption(index)} className={ghostButtonClass}>
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </section>

            <section className="col-span-12 glass rounded-sm p-5">
              <div className="flex items-center justify-between gap-4 mb-4">
                <div className="font-mono text-[16px] tracking-widest-2 text-ink">◇ PROFILE ITEMS</div>
                <button type="button" onClick={addItem} className={ghostButtonClass}>
                  Add item
                </button>
              </div>
              <div className="space-y-4 max-h-[720px] overflow-auto pr-1">
                {editor.items.map((item, index) => (
                  <div key={`item-${index}`} className="border border-line rounded-sm p-4">
                    <div className="grid grid-cols-12 gap-4">
                      <div className="col-span-12 md:col-span-2">
                        <label className={helperTextClass}>Item number</label>
                        <input
                          className={inputClass}
                          type="number"
                          value={item.id}
                          onChange={(event) => updateItem(index, "id", event.target.value)}
                        />
                      </div>
                      <div className="col-span-12 md:col-span-3">
                        <label className={helperTextClass}>Section</label>
                        <select
                          className={inputClass}
                          value={item.sectionId}
                          onChange={(event) => updateItem(index, "sectionId", event.target.value)}
                        >
                          {editor.info.sections.map((section) => (
                            <option key={section.id} value={section.id}>
                              {section.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-12 md:col-span-7">
                        <label className={helperTextClass}>Scale label</label>
                        <input
                          className={inputClass}
                          value={item.scaleLabel}
                          onChange={(event) => updateItem(index, "scaleLabel", event.target.value)}
                        />
                      </div>
                      <div className="col-span-12">
                        <label className={helperTextClass}>Item statement</label>
                        <textarea className={textAreaClass} value={item.text} onChange={(event) => updateItem(index, "text", event.target.value)} />
                      </div>
                    </div>
                    <div className="mt-3 flex justify-end">
                      <button type="button" onClick={() => removeItem(index)} className={ghostButtonClass}>
                        Remove item
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="col-span-12 glass rounded-sm p-5">
              <div className="flex items-center justify-between gap-4 mb-4">
                <div className="font-mono text-[16px] tracking-widest-2 text-ink">◇ SCORING FACTORS</div>
                <button type="button" onClick={addFactor} className={ghostButtonClass}>
                  Add factor
                </button>
              </div>
              <div className="space-y-5">
                {editor.factors.map((factor, index) => (
                  <div key={`factor-${index}`} className="border border-line rounded-sm p-4">
                    <div className="grid grid-cols-12 gap-4">
                      <div className="col-span-12 md:col-span-2">
                        <label className={helperTextClass}>Factor id</label>
                        <input
                          className={inputClass}
                          type="number"
                          value={factor.id}
                          onChange={(event) => updateFactor(index, "id", event.target.value)}
                        />
                      </div>
                      <div className="col-span-12 md:col-span-3">
                        <label className={helperTextClass}>Slug</label>
                        <input className={inputClass} value={factor.slug} onChange={(event) => updateFactor(index, "slug", event.target.value)} />
                      </div>
                      <div className="col-span-12 md:col-span-7">
                        <label className={helperTextClass}>Factor name</label>
                        <input className={inputClass} value={factor.name} onChange={(event) => updateFactor(index, "name", event.target.value)} />
                      </div>
                      <div className="col-span-12">
                        <label className={helperTextClass}>Definition</label>
                        <textarea
                          className={textAreaClass}
                          value={factor.description}
                          onChange={(event) => updateFactor(index, "description", event.target.value)}
                        />
                      </div>
                      <div className="col-span-12 md:col-span-6">
                        <label className={helperTextClass}>Primary item ids</label>
                        <input
                          className={inputClass}
                          value={toNumberListString(factor.primaryIds)}
                          onChange={(event) => updateFactorIds(index, "primaryIds", event.target.value)}
                          placeholder="1, 2, 3"
                        />
                      </div>
                      <div className="col-span-12 md:col-span-6">
                        <label className={helperTextClass}>Secondary item ids</label>
                        <input
                          className={inputClass}
                          value={toNumberListString(factor.secondaryIds)}
                          onChange={(event) => updateFactorIds(index, "secondaryIds", event.target.value)}
                          placeholder="61, 62, 63"
                        />
                      </div>

                      {[
                        { label: "Primary mid", field: "primaryMid" },
                        { label: "Secondary mid", field: "secondaryMid" },
                        { label: "Variance mid", field: "varianceMid" },
                      ].map((rangeField) => (
                        <div key={`${factor.id}-${rangeField.field}`} className="col-span-12 md:col-span-4">
                          <label className={helperTextClass}>{rangeField.label}</label>
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              className={inputClass}
                              type="number"
                              value={factor[rangeField.field][0]}
                              onChange={(event) => updateFactorRange(index, rangeField.field, 0, event.target.value)}
                            />
                            <input
                              className={inputClass}
                              type="number"
                              value={factor[rangeField.field][1]}
                              onChange={(event) => updateFactorRange(index, rangeField.field, 1, event.target.value)}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 flex justify-end">
                      <button type="button" onClick={() => removeFactor(index)} className={ghostButtonClass}>
                        Remove factor
                      </button>
                    </div>
                  </div>
                ))}
                {!editor.factors.length ? (
                  <div className="border border-dashed border-line rounded-sm p-4 text-base text-ink-2">
                    Add factors here when this profile is ready for scoring and report generation.
                  </div>
                ) : null}
              </div>
            </section>
          </div>
        </div>
      </section>
        </>
      ) : null}
    </main>
  );
};

export default AdminDashboard;
